// MiniDeadlinesCalendar.jsx
import React, { useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { Link } from "wouter";

dayjs.locale("it");

/**
 * docDeadlines / contractDeadlines: array di date (stringa ISO o Date)
 */
export default function MiniDeadlinesCalendar({
  docDeadlines = [],
  contractDeadlines = [],
  signedDocDeadlines = [],
  month, // <-- dayjs/Date/string
}) {
  const currentMonth = useMemo(
    () => (month ? dayjs(month).startOf("month") : dayjs().startOf("month")),
    [month]
  );

  const today = dayjs().startOf("day");  
  const monthParam = currentMonth.format("YYYY-MM-DD");

  const days = useMemo(() => {
    const start = currentMonth.startOf("week"); // lunedì
    const end = currentMonth.endOf("month").endOf("week");
    const out = [];
    let d = start.clone();
    while (d.isBefore(end) || d.isSame(end, "day")) {
      out.push(d);
      d = d.add(1, "day");
    }
    return out;
  }, [currentMonth]);

  // mappa YYYY-MM-DD -> counts
  const docByDay = useMemo(() => {
    const map = {};
    docDeadlines.forEach((dt) => {
      const d = dayjs(dt);
      if (!d.isSame(currentMonth, "month")) return;
      const ymd = d.format("YYYY-MM-DD");
      map[ymd] = (map[ymd] || 0) + 1;
    });
    return map;
  }, [docDeadlines, currentMonth]);

  const ctrByDay = useMemo(() => {
    const map = {};
    contractDeadlines.forEach((dt) => {
      const d = dayjs(dt);
      if (!d.isSame(currentMonth, "month")) return;
      const ymd = d.format("YYYY-MM-DD");
      map[ymd] = (map[ymd] || 0) + 1;
    });
    return map;
  }, [contractDeadlines, currentMonth]);

  const signedDocsByDay = useMemo(() => {
    const map = {};

    signedDocDeadlines.forEach((dt) => {
      if (!dt) return;

      const d = dayjs(dt);
      if (!d.isValid() || !d.isSame(currentMonth, "month")) return;

      const ymd = d.format("YYYY-MM-DD");
      map[ymd] = (map[ymd] || 0) + 1;
    });

    return map;
  }, [signedDocDeadlines, currentMonth]);

  const docsMonthCount = Object.values(docByDay).reduce((a, b) => a + b, 0);
  const ctrsMonthCount = Object.values(ctrByDay).reduce((a, b) => a + b, 0);
  const signedDocsMonthCount = Object.values(signedDocsByDay).reduce((a, b) => a + b, 0);

  const weekDays = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];

  return (
    <div className="rounded-b-md border border-gray-200 bg-white shadow-sm">
      {/* intestazione giorni settimana */}
      <div className="grid grid-cols-7 text-[10px] font-semibold text-indigo-500 mb-0 pt-1">
        {weekDays.map((d) => (
          <div key={d} className="text-center">
            {d}
          </div>
        ))}
      </div>

      {/* giorni */}
      <div className="grid grid-cols-7 gap-y-1 text-xs">
        {days.map((d) => {
          const ymd = d.format("YYYY-MM-DD");
          const isCurrentMonth = d.isSame(currentMonth, "month");
          const isToday = d.isSame(today, "day");
          const docCount = docByDay[ymd] || 0;
          const ctrCount = ctrByDay[ymd] || 0;
          const signedDocCount = signedDocsByDay[ymd] || 0;

          const dots = [];
          if (docCount > 0) {
            dots.push(
              <span
                key="doc"
                className="w-1.5 h-1.5 rounded-full bg-indigo-500"
              />
            );
          }
          if (ctrCount > 0) {
            dots.push(
              <span
                key="ctr"
                className="w-1.5 h-1.5 rounded-full bg-amber-500"
              />
            );
          }
          if (signedDocCount > 0) {
            dots.push(
              <span
                key="signed-doc"
                className="w-1.5 h-1.5 rounded-full bg-green-500"
              />
            );
          }

          return (
            <div
              key={ymd}
              className={`
                relative flex items-center justify-center
                h-4 rounded-md
                ${isCurrentMonth ? "text-gray-900" : "text-gray-300"}
                ${isToday ? "bg-indigo-50 font-semibold" : ""}
              `}
            >
              <Link to={`/deadlines?month=${monthParam}`}>
              {/* contenitore numero + pallini */}
              <div className="flex items-center gap-1 cursor-pointer">
                <span className="text-[11px] leading-none">{d.date()}</span>

                {/* pallini */}
                {dots.length > 0 && (
                  <div className="flex flex-col gap-0.5">
                    {dots}
                  </div>
                )}
              </div>
              </Link>
            </div>
          );
        })}
      </div>

      {/* riepilogo numerico mese */}
      <div className="mt-3 px-2 text-[11px] flex flex-wrap items-center justify-between gap-x-3 gap-y-2 pb-2">
        <div className="flex items-center gap-1 text-gray-600">
          <span className="w-2 h-2 rounded-full bg-indigo-500" />
          <span>Documenti</span>
          <span className="font-semibold ml-1">{docsMonthCount}</span>
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-1 text-gray-600">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          <span>Contratti</span>
          <span className="font-semibold ml-1">{ctrsMonthCount}</span>
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-1 text-gray-600">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span>Documenti con firma</span>
          <span className="font-semibold ml-1">{signedDocsMonthCount}</span>
        </div>
      </div>
    </div>
  );
}